import { Flex, Group, MultiSelect, Select, Stack, Text } from '@mantine/core';
import { DateInput, DateValue } from '@mantine/dates';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import i18next from 'i18next';
import { Timeframes } from 'models/filter';
import { InfoIcon } from 'components/InfoIcon';
import { usePatientDetailContext } from 'context/hooks';
import { Symptom } from 'models/generic';
import { PatientDetailsDescriptionKeys } from 'models/measurement';
import { formatDate, generalDateFormat, getEnlistmentKey } from 'utils/dates';

interface FilterProps {
  pageDescriptionKey?: string;
}

export const PatientDetailsFilters = ({ pageDescriptionKey }: FilterProps) => {
  const { t } = useTranslation();
  const {
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    timeframe,
    changeTimeFrame,
    symptoms,
    setSymptoms,
    prescriptions,
    selectedPrescriptionKey,
    selectPrescription,
    clearPrescriptionSelection,
  } = usePatientDetailContext();

  const handleStartDateInputChange = (e: DateValue) => {
    if (e && endDate && e > endDate) {
      setEndDate(dayjs(e).endOf('day').toDate());
    }
    setStartDate(dayjs(e).startOf('day').toDate());
    changeTimeFrame(Timeframes.Custom);
    clearPrescriptionSelection();
  };

  const handleEndDateInputChange = (e: DateValue) => {
    if (e && startDate && e < startDate) {
      setStartDate(dayjs(e).startOf('day').toDate());
    }
    setEndDate(dayjs(e).endOf('day').toDate());
    changeTimeFrame(Timeframes.Custom);
    clearPrescriptionSelection();
  };

  const testKey = pageDescriptionKey?.replace('_tooltip', '').replace('_', '-');

  return (
    <Flex p={'sm'} mb={'md'} justify={'space-between'} align={'center'}>
      <Group>
        <DateInput
          locale={i18next.language}
          value={startDate}
          onChange={(e) => {
            handleStartDateInputChange(e);
          }}
          valueFormat={generalDateFormat}
          label={t('patients_filter_start_date')}
          data-testid={`input-start-date-${testKey}`}
        />
        <DateInput
          locale={i18next.language}
          value={endDate}
          onChange={(e) => {
            handleEndDateInputChange(e);
          }}
          valueFormat={generalDateFormat}
          label={t('patients_filter_end_date')}
          data-testid={`input-end-date-${testKey}`}
        />
        <Select
          label={t('timeframe_label')}
          value={timeframe}
          onChange={(value) => changeTimeFrame(value as Timeframes)}
          data={Object.values(Timeframes).map((value) => ({
            value,
            label: t(`timeframe_${value}`),
          }))}
          data-testid={`select-timeframe-${testKey}`}
        />
        {pageDescriptionKey?.includes(PatientDetailsDescriptionKeys.HeartRate) && (
          <Select
            label={t('patients_filter_prescription')}
            value={selectedPrescriptionKey}
            onChange={(value) => value && selectPrescription(value)}
            data={prescriptions.map((prescription) => ({
              value: getEnlistmentKey(prescription),
              label: prescription.name,
            }))}
            maxDropdownHeight={500}
            renderOption={({ option, checked }) => {
              const prescription = prescriptions.find((p) => getEnlistmentKey(p) === option.value);
              if (!prescription) return option.label;
              return (
                <Stack gap={0}>
                  <Text size={'sm'} c={checked ? 'white' : undefined}>
                    {option.label}
                  </Text>
                  <Text size={'xs'} c={checked ? 'gray.3' : 'dimmed'}>
                    {`${formatDate({ timestamp: prescription.creationTimestamp })} - ${formatDate({ timestamp: prescription.expiryTimestamp })}`}
                  </Text>
                </Stack>
              );
            }}
            data-testid={`select-prescription-${testKey}`}
          />
        )}
        {pageDescriptionKey?.includes(PatientDetailsDescriptionKeys.BloodPressure) && (
          <MultiSelect
            label={t('symptoms_label')}
            value={symptoms}
            onChange={(value) => setSymptoms(value)}
            styles={{ root: { width: 200 } }}
            data={Object.values(Symptom).map((v) => ({
              value: v,
              label: t(`symptom_${v}`),
            }))}
            placeholder={t('filter_by_placeholder')}
            data-testid={`select-symptoms-${testKey}`}
          />
        )}
      </Group>
      {pageDescriptionKey && <InfoIcon i18nKey={pageDescriptionKey} testId={`info-icon-${testKey}`} />}
    </Flex>
  );
};
